import React, { useEffect, useState, useRef } from 'react';
import { Check, Zap, X } from 'lucide-react';
const pricingPlans = [{
  name: 'Starter',
  price: '$0',
  period: 'forever',
  description: 'Perfect for testing the waters. Start with 5 essential tools and see results before upgrading.',
  features: ['Product Badges (2 badges limit)', 'Multi-Item Discounts (2 rules limit)', 'Wishlist System (full functionality)', 'Side Cart Drawer (full functionality)', 'Product Gallery (image slider)', 'Community support forum', 'Regular update schedule'],
  limitations: ['Limited customization options', 'No premium tools access'],
  popular: false,
  cta: 'Start Free Now'
}, {
  name: 'Professional',
  price: '$79',
  period: 'per year',
  description: 'Everything you need to maximize conversions. Perfect for growing stores ready to scale.',
  features: ['Everything in Starter, UNLIMITED', 'All 12 tools fully unlocked', 'Store-Wide Flash Sales', 'AJAX Instant Search', 'Recently Viewed Products', 'Save for Later', 'Product Video Gallery', 'Advanced Product Reviews', 'Wholesale Pricing System', 'Digital Gift Cards', 'Priority support', '12 months updates', '<strong>1 site</strong> license'],
  limitations: [],
  popular: false,
  cta: 'Get Professional'
}, {
  name: 'Business',
  price: '$149',
  period: 'per year',
  description: 'Best value for agencies and multi-store owners. Deploy across multiple sites with ease.',
  features: ['Everything in Professional', 'All 12 tools fully unlocked', 'Priority support (faster response)', '12 months updates included', '<strong>5 sites</strong> licenses', 'Perfect for small agencies', 'Manage multiple stores', 'Volume discount savings'],
  limitations: [],
  popular: true,
  cta: 'Get Business'
}, {
  name: 'Enterprise',
  price: '$199',
  period: 'per year',
  description: 'Ultimate freedom for agencies. Install on unlimited sites and scale without limits.',
  features: ['Everything in Business', 'All 12 tools fully unlocked', 'VIP support (24-hour guarantee)', '12 months updates included', '<strong>UNLIMITED sites</strong> licenses', 'Perfect for agencies', 'Deploy across all clients', 'Agency partner badge', 'Maximum ROI potential'],
  limitations: [],
  popular: false,
  cta: 'Get Enterprise'
}];
export function Pricing() {
  const pricingRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.disconnect();
      }
    }, {
      threshold: 0.1
    });
    if (pricingRef.current) {
      observer.observe(pricingRef.current);
    }
    return () => {
      if (pricingRef.current) {
        observer.disconnect();
      }
    };
  }, []);
  return <section id="pricing" className="py-20 md:py-32 bg-white" ref={pricingRef} data-id="element-230">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl" data-id="element-231">
        <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-232">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 tracking-tight" data-id="element-233">
            Pricing That{' '}
            <span className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent" data-id="element-234">
              Pays for Itself
            </span>
          </h2>
          <p className="text-xl text-gray-600" data-id="element-235">
            Start with 5 tools FREE forever. Upgrade to unlock all 12 tools when
            you're ready. Most Pro users recover their investment from their
            first sale.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto items-center lg:py-8" data-id="element-236">
          {pricingPlans.map((plan, index) => <div key={index} className={`relative bg-white rounded-2xl overflow-hidden transition-all duration-700 transform ${plan.popular ? 'border-2 border-purple-600 shadow-2xl lg:scale-105 lg:-my-4' : 'border border-gray-200 hover:border-gray-300 hover:shadow-xl'} ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`} style={{
          transitionDelay: `${index * 100}ms`
        }} data-id="element-237">
              {plan.popular && <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white text-center py-2.5 text-sm font-medium flex items-center justify-center gap-2" data-id="element-238">
                  <Zap className="h-4 w-4" data-id="element-239" />
                  Most popular
                </div>}

              <div className="p-6 flex flex-col h-full" data-id="element-240">
                <div className="flex-grow" data-id="element-241">
                  <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-242">
                    {plan.name}
                  </h3>
                  <div className="flex items-baseline mb-4" data-id="element-243">
                    <span className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent" data-id="element-244">
                      {plan.price}
                    </span>
                    <span className="text-gray-500 ml-2 text-sm" data-id="element-245">
                      {plan.period}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-6 leading-relaxed text-sm" data-id="element-246">
                    {plan.description}
                  </p>

                  <ul className="space-y-3 mb-6" data-id="element-247">
                    {plan.features.map((feature, i) => <li key={i} className="flex items-start" data-id="element-248">
                        <Check className="h-4 w-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-249" />
                        <span className="text-gray-700 text-sm" dangerouslySetInnerHTML={{
                    __html: feature
                  }} data-id="element-250" />
                      </li>)}
                    {plan.limitations.map((limitation, i) => <li key={`limit-${i}`} className="flex items-start" data-id="element-251">
                        <X className="h-4 w-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-252" />
                        <span className="text-gray-500 text-sm" data-id="element-253">
                          {limitation}
                        </span>
                      </li>)}
                  </ul>
                </div>

                <a href="#buy-now" className={`block text-center py-3 px-6 rounded-lg font-medium transition-all text-sm ${plan.popular ? 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white shadow-lg shadow-purple-500/25' : 'bg-gray-100 hover:bg-gray-200 text-gray-900'}`} data-id="element-254">
                  {plan.cta}
                </a>
              </div>
            </div>)}
        </div>

        <div className="mt-20 text-center" data-id="element-255">
          <div className="inline-flex flex-wrap items-center justify-center gap-8 text-sm text-gray-600" data-id="element-256">
            <span className="flex items-center gap-2" data-id="element-257">
              <svg className="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20" data-id="element-258">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-259" />
              </svg>
              100% Money-Back Guarantee (30 Days)
            </span>
            <span className="flex items-center gap-2" data-id="element-260">
              <svg className="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20" data-id="element-261">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-262" />
              </svg>
              Instant Download & Activation
            </span>
          </div>
        </div>
      </div>
    </section>;
}